import { z } from 'zod';
import { positiveDecimal, rate } from './primitives.js';

const code = z.string().trim().regex(/^[A-Z0-9_-]{2,30}$/, { error: 'Code en majuscules (A-Z, 0-9, _ -)' });
const name = z.string().trim().min(2).max(160);
const rebarMap = z.record(z.string().regex(/^HA\d{1,2}$/, { error: 'Diamètre attendu : HA6, HA8, HA10…' }), positiveDecimal);

export const concreteRecipeSchema = z.object({
  kind: z.literal('CONCRETE'),
  code,
  name,
  cementKgPerM3: positiveDecimal,
  gravelM3PerM3: positiveDecimal,
  sandM3PerM3: positiveDecimal,
  waterLPerM3: positiveDecimal.optional(),
  source: z.string().max(300).optional(),
});
export const mortarRecipeSchema = z.object({ kind: z.literal('MORTAR'), code, name, cementKgPerM2: positiveDecimal, sandLPerM2: positiveDecimal, source: z.string().max(300).optional() });
export const masonryUnitSchema = z.object({
  kind: z.literal('MASONRY_UNIT'),
  code,
  name,
  materialCode: code,
  unitsPerM2: positiveDecimal,
  jointMortar: code.optional(),
  supplyMode: z.enum(['PURCHASE', 'ON_SITE']),
  making: z.object({ cementKgPerUnit: positiveDecimal, sandLPerUnit: positiveDecimal }).optional(),
  source: z.string().max(300).optional(),
});
export const slabSystemSchema = z.object({
  kind: z.literal('SLAB_SYSTEM'),
  code,
  name,
  blockMaterialCode: code,
  blocksPerM2: positiveDecimal,
  joistMlPerM2: positiveDecimal,
  joistMode: z.enum(['CAST', 'PREFAB']),
  joistConcreteM3PerMl: positiveDecimal.optional(),
  joistMaterialCode: code.optional(),
  joistRebarMlPerMl: rebarMap.optional(),
  source: z.string().max(300).optional(),
});
/** Composition d'ouvrage : matériaux par unité d'ouvrage (m², m³, ml, U), mortier facultatif. */
export const kitRecipeSchema = z.object({
  kind: z.literal('KIT'),
  code,
  name,
  per: z.enum(['m2', 'm3', 'ml', 'U']),
  output: z.string().regex(/^[\p{L}_][\p{L}\p{N}_]*$/u).optional(),
  materials: z.array(z.object({ code, quantity: positiveDecimal, note: z.string().max(200).optional() })).max(30),
  mortar: code.optional(),
  source: z.string().max(300).optional(),
});
export const recipeSchema = z.discriminatedUnion('kind', [concreteRecipeSchema, mortarRecipeSchema, masonryUnitSchema, slabSystemSchema, kitRecipeSchema]);

export const rebarSpecSchema = z.discriminatedUnion('method', [
  z.object({ method: z.literal('RATIO_KG_M3'), kgPerM3: positiveDecimal, split: rebarMap }),
  z.object({ method: z.literal('RATIO_ML_ML'), base: z.string().trim().min(1).max(300), mlPerUnit: rebarMap, usableLength: positiveDecimal.optional() }),
  z.object({
    method: z.literal('DETAILED'),
    bars: z.array(z.object({ label: z.string().trim().min(1).max(80), material: z.string().regex(/^HA\d{1,2}$/), count: z.string().trim().min(1).max(200), length: z.string().trim().min(1).max(200) })).min(1).max(50),
  }),
]);

export const typeResourcesSchema = z.object({
  concrete: code.optional(),
  blinding: code.optional(),
  masonry: code.optional(),
  /** « NONE » : aucun mortier (peinture…). */
  mortar: code.optional(),
  slab: code.optional(),
  rebar: rebarSpecSchema.optional(),
  kits: z.array(code).max(10).optional(),
});

export const materialSchema = z.object({
  code,
  name,
  category: z.enum(['LIANT', 'GRANULAT', 'EAU', 'ACIER', 'BLOC', 'PLANCHER', 'BOIS', 'QUINCAILLERIE', 'ETANCHEITE', 'COUVERTURE', 'REVETEMENT', 'PEINTURE', 'FAUX_PLAFOND', 'AUTRE']),
  unit: z.string().trim().min(1).max(20),
  purchaseCeil: z.boolean().optional(),
  lossRate: rate.optional(),
  bagKg: positiveDecimal.optional(),
  diameterMm: z.number().int().positive().optional(),
  kgPerM: positiveDecimal.optional(),
  noCost: z.boolean().optional(),
});

export const priceSchema = z.object({
  unitPrice: positiveDecimal,
  currency: z.enum(['XAF', 'XOF', 'EUR', 'USD']).default('XAF'),
  priceDate: z.iso.date(),
  source: z.enum(['SUPPLIER', 'MANUAL', 'LAST_USED', 'IMPORT']).default('MANUAL'),
  supplier: z.string().trim().max(160).nullable().optional(),
  location: z.string().trim().max(160).nullable().optional(),
  note: z.string().max(1000).nullable().optional(),
});
